import React, {RefObject} from 'react';
import { FacetProps } from '../Facets';
import DatePicker, {ReactDatePickerProps} from 'react-datepicker';
import {DateRange, FacetItem, FacetModel} from '../../../../types/Search';
import TimeKeeper from 'react-timekeeper';
import moment from 'moment';

import 'react-datepicker/dist/react-datepicker.css';
import './DateRangeFacet.scss';

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

enum PickerType {
  START_DATE = 'StartDatePicker',
  END_DATE = 'EndDatePicker'
}

const FormatBasedProperties: any = {
  Time: {
    dateFormat: 'HH:mm:ss',
    showTimeSelectOnly: true,
    showTimeInput: true,
    customTimeInput: '$REPLACE$',
    timeInputLabel: ''
  },
  DateTime: {
    dateFormat: `yyyy-MM-dd HH:mm:ss`,
    showFullMonthYearPicker: true,
    showTimeInput: true,
    customTimeInput: '$REPLACE$',
    timeInputLabel: ''
  },
  Date: {
    dateFormat: 'yyyy-MM-dd'
  },
  Year: {
    dateFormat: 'yyyy',
    showYearPicker: true,
    // overrides the custom header that is added for the other formats
    renderCustomHeader: undefined
  },
  YearMonth: {
    dateFormat: 'yyyy-MM',
    showMonthYearPicker: true
  }
};

const MOMENT_FORMATTERS: any = {
  Time: 'HH:mm:ssZ',
  DateTime: `YYYY-MM-DDTHH:mm:ss.SSSZ`,
  // the upper case is because for moment 'dd' will return the day as name not number
  Date: FormatBasedProperties.Date.dateFormat.toUpperCase(),
  Year: FormatBasedProperties.Year.dateFormat.toUpperCase(),
  YearMonth: FormatBasedProperties.YearMonth.dateFormat.toUpperCase()
}

const LABELS: any = {
  Time: 'time',
  DateTime: 'date-time',
  Date: 'date',
  Year: 'year',
  YearMonth: 'year-month',
}

/**
 * Component which renders a date range selection date facet.
 * Shows total number of documents as well as doc count per year/per month
 * as returned by the search aggregation.
 */
class DateRangeFacet extends React.Component<FacetProps> {
  private startDate: Date;
  private endDate: Date;
  private items: { [key: string]: FacetItem };
  private yearsToDocCountMap: Map<number, { total: number, monthsToDocMap: Map<number, number> }>;
  private readonly startDatePickerRef: RefObject<DatePicker> = React.createRef();
  private readonly endDatePickerRef: RefObject<DatePicker> = React.createRef();

  constructor(props: FacetProps) {
    super(props);
    const {items, maxDate, minDate} = this.buildItemsModel(this.props.model);
    this.yearsToDocCountMap = this.buildYearsToDocCountMap(items);
    this.startDate = minDate;
    this.endDate = maxDate;
    this.items = items;
  }

  private buildItemsModel(facetModel: FacetModel) {
    const items: { [key: string]: FacetItem } = facetModel.data;
    let minDate: Date = new Date();
    let maxDate: Date = new Date();

    Object.values(items).forEach(item => {
      const itemDate = DateRangeFacet.getUTCDate(item.key);
      if (!minDate || itemDate < minDate) {
        minDate = itemDate;
      }
      if (!maxDate || itemDate > maxDate) {
        maxDate = itemDate;
      }
    })
    return {items, minDate, maxDate};
  }

  /**
   * Converts the input value to Date object without applying the local offset (UTC).
   *
   * @param value to be converted
   * @returns {Date}
   * @private
   */
  private static getUTCDate(value: any): Date {
    const itemDate = new Date(value);
    const offset = itemDate.getTimezoneOffset() * 60 * 1000;
    return new Date(itemDate.getTime() + offset);
  }

  private buildYearsToDocCountMap(items: { [key: string]: FacetItem }): Map<number, { total: number, monthsToDocMap: Map<number, number> }> {
    const yearsToDocCountMap = new Map<number, { total: number, monthsToDocMap: Map<number, number> }>();
    Object.values(items).forEach((item: FacetItem) => {
      const date = new Date(item.key);
      const itemYear = date.getFullYear();
      const itemMonth = date.getMonth();
      const existingYear = yearsToDocCountMap.get(itemYear);
      if (existingYear) {
        const totalCount = existingYear.total + item.count;
        const monthsToDocMap = existingYear.monthsToDocMap;
        const monthCount = monthsToDocMap.get(itemMonth);
        if (monthCount) {
          monthsToDocMap.set(itemMonth, monthCount + item.count)
        } else {
          monthsToDocMap.set(itemMonth, item.count)
        }
        yearsToDocCountMap.set(itemYear, {total: totalCount, monthsToDocMap});
      } else {
        const monthsToDocMap = new Map<number, number>();
        monthsToDocMap.set(itemMonth, item.count);
        yearsToDocCountMap.set(itemYear, {total: item.count, monthsToDocMap});
      }
    });
    return yearsToDocCountMap;
  }

  private updateItemModel() {
    const {items, maxDate, minDate} = this.buildItemsModel(this.props.model);
    this.yearsToDocCountMap = this.buildYearsToDocCountMap(items);

    this.startDate = this.startDate || minDate;
    this.endDate = this.endDate || maxDate
    this.items = items;
  }

  /**
   * Simple setter for the start date.
   */
  private readonly setStartDate = (date: Date) => {
    this.startDate = date;
  }

  /**
   * Simple setter for the end date.
   */
  private readonly setEndDate = (date: Date) => {
    this.endDate = date;
  }

  /**
   * Triggers the search (filtering) when enter key is pressed. Used when the dates are inputted manually.
   */
  private readonly selectOnEnterKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      this.onSelect();
    }
  }

  /**
   * Updates the model of the current facet and triggers search operation.
   */
  private readonly onSelect = () => {
    if (!this.startDate || !this.endDate) {
      return;
    }
    const {title, fieldType, field, facetType} = this.props.model;
    const dateRange: DateRange = {
      startDate: DateRangeFacet.formatDate(fieldType, this.startDate),
      endDate: DateRangeFacet.formatDate(fieldType, this.endDate)
    };
    this.props.onSelect({key: title, field, fieldType, facetType, value: dateRange});
  }

  private static formatDate(range: string, date?: Date): string {
    if (!date) {
      return '';
    }
    return moment(date).format(MOMENT_FORMATTERS[range]);
  }

  private readonly getYearsOptions = (years: Array<number>) => {
    return years.map(option => {
      const optionCount = this.yearsToDocCountMap.get(option)?.total;
      return this.getOption(option, optionCount);
    });
  }

  private readonly getMonthsOptions = (date: Date) => {
    const selectedYear = date.getFullYear();
    return months.map(option => {
      const optionMonth = months.indexOf(option);
      const count = this.yearsToDocCountMap.get(selectedYear)?.monthsToDocMap.get(optionMonth)
      return this.getOption(option, count);
    });
  }

  private readonly getOption = (option: string | number, count?: number) => {
    const countString = ` - (${count})`;
    return (
      <option key={option} value={option}>
        {`${option}${count ? countString : ''}`}
      </option>
    )
  }

  private readonly buildCustomHeaderElement = () => {
    const years: Array<number> = [];
    const startDateYear = this.startDate.getFullYear();
    const endDateYear = this.endDate.getFullYear();

    for (let i = startDateYear; i <= endDateYear; i++) {
      years.push(i);
    }

    return ({
              date,
              changeYear,
              changeMonth,
              decreaseMonth,
              increaseMonth,
              prevMonthButtonDisabled,
              nextMonthButtonDisabled
            }: any) => (
      <div className="date-picker-header">
        <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>{'<'}</button>
        <select className="year-select"
                value={date.getFullYear()}
                onChange={({target: {value}}) => changeYear(+value)}>
          {this.getYearsOptions(years)}
        </select>
        <select className="month-select"
                value={months[date.getMonth()]}
                onChange={({target: {value}}) => changeMonth(months.indexOf(value))}>
          {this.getMonthsOptions(date)}
        </select>
        <button onClick={increaseMonth} disabled={nextMonthButtonDisabled}>{'>'}</button>
      </div>
    )
  }

  private readonly buildDatePickerProperties = (pickerType: PickerType): ReactDatePickerProps => {
    const isStartDateType = PickerType.START_DATE === pickerType;
    const properties = {
      onChange: isStartDateType ? this.setStartDate: this.setEndDate,
      onSelect: this.onSelect,
      onKeyDown: this.selectOnEnterKey,
      selected: isStartDateType ? this.startDate : this.endDate,
      dropdownMode: 'select',
      portalId: 'DateRangeFacetPortal',
      renderCustomHeader: this.buildCustomHeaderElement(),
      calendarClassName: `${pickerType} date-picker`,
      selectsStart: isStartDateType,
      selectsEnd: !isStartDateType,
      startDate: this.startDate,
      endDate: this.endDate,
      // this properties should be added at the end, because they may override some of the defined above
      ...FormatBasedProperties[this.props.model.fieldType]
    };

    const pickerRef = isStartDateType ? this.startDatePickerRef : this.endDatePickerRef;
    this.decoratePickerProperties(properties, pickerRef);
    return properties;
  };

  private readonly decoratePickerProperties = (properties: ReactDatePickerProps, pickerRef: RefObject<DatePicker>) => {
    addCustomTimePicker(properties, pickerRef);
  }

  render() {
    this.updateItemModel();

    const label = LABELS[this.props.model.fieldType];
    return (
      <div className="DateRangeFacet">
        <div className="start-date">
          <div>Start {label}:</div>
          <DatePicker ref={this.startDatePickerRef} {...this.buildDatePickerProperties(PickerType.START_DATE)}/>
        </div>
        <div className="end-date">
          <div>End {label}:</div>
          <DatePicker ref={this.endDatePickerRef} {...this.buildDatePickerProperties(PickerType.END_DATE)}/>
        </div>
      </div>
    )
  }
}

/**
 * Replaces the value of the 'properties.customTimeInput', if the current one is '$REPLACE$' with custom component
 * for time input.
 *
 * @param {ReactDatePickerProps} properties to be decorated
 * @param {RefObject<DatePicker>} pickerRef reference to the picker element
 */
function addCustomTimePicker(properties: ReactDatePickerProps, pickerRef: RefObject<DatePicker>): void {
  const timeInput = properties.customTimeInput;
  if (!timeInput || timeInput !== '$REPLACE$') {
    return;
  }

  const toDate = (hours: number, minutes: number) => {
    const date = properties.selected || new Date();
    date.setHours(hours);
    date.setMinutes(minutes);
    // can't be used to select seconds, they can be input manually
    date.setSeconds(0);
    return date;
  };

  // setStartDate or setEndDate based on the PickerType
  const onChangeFn: any = properties.onChange;
  const onSelectFn: any = properties.onSelect;
  const currentDate = new Date();
  const CustomTimePicker = () => <TimeKeeper onChange={(time) => onChangeFn(toDate(time.hour, time.minute))}
                                             onDoneClick={() => {
                                               onSelectFn();
                                               // closes the main picker when the 'Done' btn is clicked
                                               if (pickerRef && pickerRef.current) {
                                                 pickerRef.current.setOpen(false);
                                               }
                                             }}
                                             // shows the current time, when the picker is rendered
                                             time={{hour: currentDate.getHours(), minute: currentDate.getMinutes()}}
                                             hour24Mode
                                             switchToMinuteOnHourSelect/>;

  properties.customTimeInput = <CustomTimePicker/>;
}

export default DateRangeFacet;
